import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { RecipientsSummaryList } from '../../components/RecipientsSummaryList/RecipientsSummaryList';
import { SummaryTab } from '../../components/SummaryTab/SummaryTab';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { useConfirmation } from '../../utils/ConfirmationServiceContext/confirmationContext';
import { translate } from '../../utils/translate';
import { setIncomingMessages } from '../ChatsList/chatListSlice';
import { selectGroupById } from '../GroupsList/groupsSlice';
import { TEXT_LENGTH_LIMIT, useStartOnCallAlertMessageCtx } from './StartOnCallAlertMessageContext';
import Pencil from '../../assets/icons/pen-with-side-line--yellow.svg';
import { SIcon, SInput, SItem, SSimpleText, SLine } from './styles';
import { CecChatInput } from '../../components/cec/CecChatInput/CecChatInput';
import { FilterOrSelectBottomSheet } from '../../components/FilterOrSelectBottomSheet/FilterOrSelectBottomSheet';
import { useTenantId } from '../../utils/customHooks/useTenantId';
import { useGetMembers } from './helpers';
import { fetchGroups } from '../GroupsList/groupsSlice/actionCreators';
import { fetchOnCallAlertList } from '../OnCallAlertList/onCallAlertSlice/actionCreators';
import { sendOnCallAlertMessage } from '../../apis/onCallAlertApi';
import Loader from '../../components/Loader/Loader';
import { selectUserId } from '../Login/LoginSlice';
import { getRecipientsText } from '../StartIamOkMessage/helpers';
import type { SelectedAllType } from '../../components/FilterOrSelectBottomSheet/types';

const SContainer = styled.div`
  width: 90%;
  margin: auto;
`;

export const SSummaryTab = styled(SummaryTab)`
  /* padding: 0.9375rem 0rem; */
  .SRightContaoner {
    width: 77%;
  }
`;

export const StartOnCallAlertMessageSummary = () => {
  const dispatch = useAppDispatch();
  const navigation = useNavigate();
  const confirm = useConfirmation();
  const tenantId = useTenantId();
  const userId = useAppSelector(selectUserId);

  const [isLoading, setIsLoading] = React.useState(false);
  const [recipientsFilter, setRecipientsFilter] = React.useState(false);

  const subjectRef = React.useRef<HTMLInputElement>(null);

  const {
    groupIds,
    setGroupIds,
    subject,
    setSubject,
    onCallAlertMessageText: text,
    setOnCallAlertMessageText: setText,
    userIds,
    setUserIds,
  } = useStartOnCallAlertMessageCtx();
  const { members, isGettingMembers } = useGetMembers({
    id: groupIds || 0,
    setUserIds,
    tenantId,
  });
  const goToSelectGroupList = () => {
    navigation('/startOnCallAlert');
  };

  const gotToOnCallAler = (id: number) => {
    navigation(`/oncall/${id}`);
  };

  React.useEffect(() => {
    if (!groupIds) {
      goToSelectGroupList();
      return;
    }
  }, [groupIds]);

  const selectedGroup = useAppSelector(selectGroupById(groupIds || 0));

  const handleDelete = () => setGroupIds(undefined);
  const handleSubject = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSubject(e.target.value);
  };

  const onFilter = (selected: Set<number> | SelectedAllType) => {
    switch (selected) {
      case 'SELECTED_ALL': {
        const selectedMembers = new Set(members.map(member => member.userID));
        setUserIds(selectedMembers);
        setRecipientsFilter(false);
        break;
      }
      case 'UNSELECTED_ALL':
        break;
      default:
        setUserIds(selected);
        setRecipientsFilter(false);
        break;
    }
  };

  const handleMessageSend = async (textMsg: string) => {
    try {
      setIsLoading(true);
      const { id } = await sendOnCallAlertMessage({
        groupId: groupIds || 0,
        text: textMsg,
        recipientIDs: Array.from(userIds),
        subject,
      });
      setIsLoading(false);
      dispatch(fetchOnCallAlertList());
      dispatch(fetchGroups());
      dispatch(setIncomingMessages(true));
      if (id) gotToOnCallAler(id);
    } catch (e) {
      setIsLoading(false);
      confirm({
        title: 'warning',
        description: e as 'string | undefined',
        onCancel: () => {},
        confirmText: 'retry',
        cancelText: 'cancel',
      });
    }
  };

  const highlightText = () => {
    if (subjectRef.current) subjectRef.current.focus();
  };

  if (isLoading) return <Loader />;
  return (
    <SContainer>
      {selectedGroup && (
        <RecipientsSummaryList data={[selectedGroup]} handleRemoveClick={handleDelete} />
      )}
      <SLine />
      <SSummaryTab
        title={translate('messages_to')}
        summaryText={getRecipientsText(members, userIds || new Set())}
        onTabClick={() => setRecipientsFilter(true)}
      />
      <SItem className="input">
        <SSimpleText className="left" fontSize="12px">
          {translate(`messages_subject_add`)}
        </SSimpleText>
        <SInput
          onChange={handleSubject}
          value={subject}
          type="text"
          placeholder={translate(`messages_subject_add_placeholder`)}
          ref={subjectRef}
        />

        <SIcon src={Pencil} alt="" onClick={highlightText} className="pencil" />
      </SItem>

      {!isGettingMembers && (
        <FilterOrSelectBottomSheet
          isOpen={recipientsFilter}
          setIsOpen={setRecipientsFilter}
          onFilter={onFilter}
          data={members}
          initialSelected={userIds}
          withPhoto
          atLeastOneReq
          selectShapeType="box"
          titleTx="messages_selectRecepients"
          selectAllTx={'imOk_allRecepients'}
          hideCurrentUserId={userId}
        />
      )}
      <CecChatInput
        onMessageSend={handleMessageSend}
        text={text}
        setText={setText}
        limit={TEXT_LENGTH_LIMIT}
        textLength={text.length}
      />
    </SContainer>
  );
};
